import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'termsAndPrivacy',
    'sellingStandardsCheckbox',
    'modal',
    'understandButton',
    'scrollable',
    'submitButton',
    'addDistributorLink',
    'distributorsSection',
    'form',
    'sellingStandardsLink',
  ];

  connect() {
    this.modalConfirmed = false;
    this.hasReachedBottom = false;
    this.pendingCheckboxClick = false;

    this.toggleDistributors();
    this.hideFirstRemoveLinks();
    this.updateSubmitButton();

    this.modalObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const isVisible = this.modalTarget.classList.contains('in');

          if (isVisible) {
            if (!this.hasReachedBottom) this.enableUnderstandButton(false);

            this.checkScrollPosition();
          }
        }
      });
    });

    this.modalObserver.observe(this.modalTarget, {
      attributes: true,
      attributeFilter: ['class'],
    });

    if (this.hasScrollableTarget) {
      this.scrollableTarget.addEventListener(
        'scroll',
        this.checkScrollPosition.bind(this),
      );

      this.resizeObserver = new MutationObserver(() => {
        this.checkScrollPosition();
      });
      this.resizeObserver.observe(this.scrollableTarget, {
        childList: true,
        subtree: true,
      });
    }
  }

  disconnect() {
    if (this.modalObserver) this.modalObserver.disconnect();
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  handleCheckboxClick(e) {
    if (this.sellingStandardsCheckboxTarget.checked && !this.modalConfirmed) {
      e.preventDefault();
      e.stopPropagation();
      this.pendingCheckboxClick = true;
      $(this.modalTarget).modal('show');
    } else {
      this.updateSubmitButton();
    }
  }

  handleUnderstandClick() {
    this.modalConfirmed = true;
    $(this.modalTarget).modal('hide');

    if (this.pendingCheckboxClick) {
      this.sellingStandardsCheckboxTarget.checked = true;
      this.updateSubmitButton();
      this.toggleDistributors();
    }

    this.pendingCheckboxClick = false;
  }

  handleSellingStandardsLink(e) {
    e.preventDefault();
    e.stopPropagation();
    this.pendingCheckboxClick = true;
    $(this.modalTarget).modal('show');
  }

  handleAddDistributor(e) {
    e.preventDefault();
    this.addDistributionFields();
  }

  handleFormSubmit(e) {
    this.formTarget.querySelectorAll('.nested-fields').forEach((field) => {
      const distributor = field.querySelector(
        'select[name*="[distributor_id]"]',
      );
      const accountNumber = field.querySelector(
        'input[name*="[customer_number]"]',
      );

      const distributorEmpty = !distributor || !distributor.value;
      const accountEmpty = !accountNumber || !accountNumber.value.trim();

      if (distributorEmpty && accountEmpty) {
        field.remove();
      }
    });
  }

  handleRemoveDistributor(e) {
    e.preventDefault();
    const allFields = this.formTarget.querySelectorAll('.nested-fields');
    if (allFields.length <= 1) return;
    const field = e.target.closest('.nested-fields');
    if (field) field.remove();
  }

  enableUnderstandButton(enabled) {
    this.understandButtonTarget.disabled = !enabled;
  }

  checkScrollPosition() {
    if (!this.hasScrollableTarget || !this.hasUnderstandButtonTarget) return;

    const { scrollTop, scrollHeight, clientHeight } = this.scrollableTarget;

    // Content hasn't loaded yet — skip evaluation entirely
    if (scrollHeight < 150) return;

    const scrolledEnough =
      scrollHeight <= clientHeight + 1 ||
      scrollTop + clientHeight >= scrollHeight * 0.8;

    if (scrolledEnough) {
      this.hasReachedBottom = true;
      this.enableUnderstandButton(true);
    } else if (!this.hasReachedBottom) {
      this.enableUnderstandButton(false);
    }
  }

  updateSubmitButton() {
    const isValid =
      this.termsAndPrivacyTarget.checked &&
      this.sellingStandardsCheckboxTarget.checked;

    this.submitButtonTarget.disabled = !isValid;
  }

  toggleDistributors() {
    const enabled = this.sellingStandardsCheckboxTarget.checked;

    this.distributorsSectionTarget.style.display = enabled ? '' : 'none';
    this.distributorsSectionTarget
      .querySelectorAll('input, select, textarea')
      .forEach((el) => {
        if (el.tomselect) {
          enabled ? el.tomselect.enable() : el.tomselect.disable();
        } else {
          el.disabled = !enabled;
        }
      });
  }

  hideFirstRemoveLinks() {
    const firstLink = this.formTarget.querySelectorAll(
      '.remove-distributor',
    )[0];
    if (firstLink) firstLink.style.display = 'none';
  }

  addDistributionFields() {
    const index = document.querySelectorAll('.nested-fields').length;
    const existingField = document.querySelector('.nested-fields');
    if (!existingField) return;

    const newField = existingField.cloneNode(true);

    newField.querySelectorAll('.ts-wrapper').forEach((el) => el.remove());
    newField
      .querySelectorAll('input, select, textarea')
      .forEach((input) => this.resetField(input, index));
    newField.querySelectorAll('label[for]').forEach((label) => {
      label.htmlFor = label.htmlFor.replace(
        /_attributes_\d+_/,
        `_attributes_${index}_`,
      );
    });
    newField.querySelector('.remove-distributor').style.display = '';

    const linkContainer = this.addDistributorLinkTarget.parentElement;
    linkContainer.parentElement.insertBefore(newField, linkContainer);
  }

  resetField(input, index) {
    if (input.name) {
      input.name = input.name.replace(
        /(\[distributions_attributes])\[\d+]/,
        `$1[${index}]`,
      );
    }

    if (input.id) {
      input.id = input.id.replace(/_attributes_\d+_/, `_attributes_${index}_`);
    }

    if (
      input.type === 'hidden' &&
      input.name.includes('distributor_added_date')
    ) {
      input.value = new Date().toISOString();
    } else if (input.tagName === 'SELECT') {
      input.selectedIndex = 0;
      // Cloned from an already-initialised Tom Select row: these classes hide the
      // native select until the `select` controller reconnects and rebuilds its
      // .ts-wrapper. Strip them so the field is never left permanently invisible.
      input.classList.remove('tomselected', 'ts-hidden-accessible');
      input.removeAttribute('tabindex');
    } else {
      input.value = '';
    }
  }
}
